import React from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import styles from './styles/StockFilters.module.css';

export interface StockFilterValues {
    /** null = GLOBAL (todos los locales) */
    local: number | null;
    subcategoria: string;
    producto: string;
    estado: 'todos' | 'con_stock' | 'sin_stock' | 'bajo_minimo';
}

export const EMPTY_STOCK_FILTERS: StockFilterValues = {
    local: null,
    subcategoria: 'Todas',
    producto: '',
    estado: 'todos',
};

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface StockFiltersProps {
    values: StockFilterValues;
    onChange: (values: StockFilterValues) => void;
    onApply: () => void;
    locales: Local[];
    subcategorias: Array<{ idSubcategoria: number; nombre: string }>;
    /** Oculta el filtro de estado en vistas donde no aplica (historial). */
    showEstado?: boolean;
}

/**
 * Filtros de Stock.
 *
 * Mantiene la estetica propia de esta seccion (los chips redondeados de
 * local) pero adopta la logica de los filtros de Pedidos: varios criterios
 * se combinan y se aplican juntos, en vez de un unico filtro por vez.
 */
const StockFilters: React.FC<StockFiltersProps> = ({
    values,
    onChange,
    onApply,
    locales,
    subcategorias,
    showEstado = true,
}) => {
    const set = <K extends keyof StockFilterValues>(key: K, value: StockFilterValues[K]) =>
        onChange({ ...values, [key]: value });

    const activeCount = [
        values.local !== null,
        values.subcategoria !== 'Todas',
        values.producto.trim() !== '',
        values.estado !== 'todos',
    ].filter(Boolean).length;

    return (
        <div className={styles.filters}>
            {/* Chips de local: la estetica original de Stock */}
            <div className={styles.localChips} role="group" aria-label="Local">
                <button
                    type="button"
                    className={`${styles.chip} ${values.local === null ? styles.chipActive : ''}`}
                    onClick={() => set('local', null)}
                    aria-pressed={values.local === null}
                >
                    GLOBAL
                </button>
                {locales.map(loc => (
                    <button
                        key={loc.locales_id}
                        type="button"
                        className={`${styles.chip} ${values.local === loc.locales_id ? styles.chipActive : ''}`}
                        onClick={() => set('local', loc.locales_id)}
                        aria-pressed={values.local === loc.locales_id}
                    >
                        {loc.nombre_local.toUpperCase()}
                    </button>
                ))}
            </div>

            <div className={styles.row}>
                <div className={styles.group}>
                    <label htmlFor="stock-filtro-producto">Producto</label>
                    {/* Input ancho: es el unico campo que espera texto largo */}
                    <div className={styles.searchWrap}>
                        <FaSearch className={styles.searchIcon} aria-hidden="true" />
                        <input
                            id="stock-filtro-producto"
                            type="text"
                            className={`${styles.control} ${styles.controlWide}`}
                            placeholder="Nombre o codigo del producto"
                            value={values.producto}
                            onChange={e => set('producto', e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') onApply(); }}
                        />
                    </div>
                </div>

                <div className={styles.group}>
                    <label htmlFor="stock-filtro-subcat">Subcategoria</label>
                    <select
                        id="stock-filtro-subcat"
                        className={styles.control}
                        value={values.subcategoria}
                        onChange={e => set('subcategoria', e.target.value)}
                    >
                        <option value="Todas">Todas</option>
                        {subcategorias.map(s => (
                            <option key={s.idSubcategoria} value={s.nombre}>{s.nombre}</option>
                        ))}
                    </select>
                </div>

                {showEstado && (
                    <div className={styles.group}>
                        <label htmlFor="stock-filtro-estado">Estado</label>
                        <select
                            id="stock-filtro-estado"
                            className={styles.control}
                            value={values.estado}
                            onChange={e => set('estado', e.target.value as StockFilterValues['estado'])}
                        >
                            <option value="todos">Todos</option>
                            <option value="con_stock">Con stock</option>
                            <option value="sin_stock">Sin stock</option>
                            <option value="bajo_minimo">Bajo el minimo</option>
                        </select>
                    </div>
                )}

                <div className={styles.actions}>
                    <button type="button" className={styles.apply} onClick={onApply}>
                        Filtrar
                    </button>
                    <button
                        type="button"
                        className={styles.clear}
                        onClick={() => onChange({ ...EMPTY_STOCK_FILTERS })}
                        disabled={activeCount === 0}
                    >
                        <FaTimes aria-hidden="true" /> Limpiar
                        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StockFilters;
